import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom } from 'rxjs';
import { TenantStatusService } from './tenant-status.service';

/**
 * ResourceStatus is a string union: 'idle' | 'error' | 'loading' | 'reloading' | 'resolved' | 'local'.
 * These are the states where the resource has not yet produced a settled value.
 */
const UNSETTLED_RESOURCE_STATUSES = new Set(['idle', 'loading', 'reloading']);

/**
 * Waits for the tenant status resource to finish loading.
 *
 * On a full page reload the resource starts in `Idle` state — `isLoading()` is
 * `false` — so we must also wait when the resource has not yet left idle.
 * Without this the guard reads a `null` value (pre-fetch) and incorrectly allows
 * navigation through on reload.
 */
async function waitForStatusLoaded(tenantStatusService: TenantStatusService): Promise<void> {
  const resource = tenantStatusService.statusResource;

  const isSettled = (): boolean => !UNSETTLED_RESOURCE_STATUSES.has(resource.status());

  if (!isSettled()) {
    await firstValueFrom(toObservable(resource.status).pipe(filter(() => isSettled())));
  }
}

/**
 * Guard for all non-provisioning, non-maintenance, non-deprovisioned routes.
 *
 * Priority order:
 * 1. If tenant is deprovisioned → redirect to /deprovisioned.
 * 2. If status is 'maintenance' → redirect to /maintenance (preserving returnUrl).
 *    The old container keeps running, so users land on the maintenance page while
 *    the new container is being deployed.
 * 3. If status requires initial provisioning → redirect to /provisioning.
 * 4. Otherwise → allow navigation.
 *
 * When the browser is not on a tenant domain (e.g. plain `localhost`) the guard
 * passes through without any check.
 */
export const tenantReadyGuard: CanActivateFn = async (_route, state) => {
  const tenantStatusService = inject(TenantStatusService);
  const router = inject(Router);

  if (!tenantStatusService.host) return true;

  // Always refresh before evaluating route access.
  // The service is singleton-scoped; without a reload it can keep an outdated
  // non-ready status that was fetched before provisioning finished.
  tenantStatusService.statusResource.reload();

  await waitForStatusLoaded(tenantStatusService);

  // Deprovisioned tenants take highest priority — the tenant has been torn down
  // and no further navigation should be allowed.
  if (tenantStatusService.isDeprovisioned()) {
    return router.createUrlTree(['/deprovisioned']);
  }

  // Maintenance takes priority: redirect to /maintenance with returnUrl so the
  // user is sent back to their original page once the deploy completes.
  if (tenantStatusService.isUnderMaintenance()) {
    return router.createUrlTree(['/maintenance'], { queryParams: { returnUrl: state.url } });
  }

  // Only redirect to provisioning when the status is explicitly a non-ready
  // provisioning state. Null/unknown means the control API has no record for
  // this tenant (it was provisioned outside the control plane) — treat that
  // the same as 'ready' and let the normal auth flow handle it.
  if (tenantStatusService.requiresProvisioning()) {
    return router.createUrlTree(['/provisioning']);
  }

  return true;
};

/**
 * Guard for the provisioning page itself.
 *
 * When the tenant is already fully provisioned (`ready`), there is no reason
 * to show the provisioning page. Redirect the user to `/login` so the normal
 * auth flow (including any `returnUrl` redirect) can take over.
 *
 * When status is 'maintenance', redirect to /maintenance — the user should not
 * see the initial-provisioning flow during a rolling deploy update.
 */
export const provisioningPageGuard: CanActivateFn = async () => {
  const tenantStatusService = inject(TenantStatusService);
  const router = inject(Router);

  if (!tenantStatusService.host) return true;

  // Refresh to avoid stale singleton resource values.
  tenantStatusService.statusResource.reload();

  await waitForStatusLoaded(tenantStatusService);

  if (tenantStatusService.isUnderMaintenance()) {
    return router.createUrlTree(['/maintenance']);
  }

  // Show the provisioning page only when the tenant is actively provisioning
  // (known non-ready status). When status is null (control API has no record)
  // or 'ready', redirect to login — the tenant is operational.
  if (tenantStatusService.requiresProvisioning()) {
    return true;
  }

  return router.createUrlTree(['/login']);
};

/**
 * Guard for the /maintenance page itself.
 *
 * Allows access only when the tenant is in 'maintenance' or 'failed' status.
 * Redirects to /login when status is 'ready' (deploy completed successfully)
 * or null (no control record — tenant is operational).
 */
export const maintenancePageGuard: CanActivateFn = async () => {
  const tenantStatusService = inject(TenantStatusService);
  const router = inject(Router);

  if (!tenantStatusService.host) return true;

  tenantStatusService.statusResource.reload();

  await waitForStatusLoaded(tenantStatusService);

  const status = tenantStatusService.status();

  // Allow access while maintenance or failed (deploy error).
  if (status === 'maintenance' || status === 'failed') {
    return true;
  }

  // Tenant is ready — deploy completed, redirect to login.
  return router.createUrlTree(['/login']);
};

/**
 * Guard for the /deprovisioned page itself.
 *
 * Allows access only when the tenant is in a deprovisioning terminal state.
 * Redirects to /login when the tenant is operational.
 */
export const deprovisionedPageGuard: CanActivateFn = async () => {
  const tenantStatusService = inject(TenantStatusService);
  const router = inject(Router);

  if (!tenantStatusService.host) return true;

  tenantStatusService.statusResource.reload();

  await waitForStatusLoaded(tenantStatusService);

  if (tenantStatusService.isDeprovisioned()) {
    return true;
  }

  // Tenant is not deprovisioned — redirect to login.
  return router.createUrlTree(['/login']);
};
